/**
 * Planning a bulk token distribution.
 *
 * Pure: addresses in, groupings and costs out. No web3, no chain, so the
 * console and the CLI cannot disagree about how a list is split, what it
 * costs, or where it stopped — and so all of it is testable without a
 * validator.
 *
 * The three numbers that actually govern this:
 *
 *  - A legacy transaction is capped at 1232 bytes. A `transferChecked` is
 *    ~5 accounts; an `createAssociatedTokenAccount` ~6. So a recipient who
 *    needs an account costs roughly twice one who already has one. The limits
 *    below are deliberately under the true ceiling: a too-small batch costs one
 *    extra signature, a too-large one fails AFTER the user approved it.
 *  - A blockhash is valid for about 150 slots, roughly a minute. Signing
 *    hundreds of transactions up front and sending them over ten minutes means
 *    the later ones expire, so signing happens in waves with a fresh blockhash
 *    per wave.
 *  - Rent for a token account is about 0.00204 SOL, paid by the SENDER, for
 *    every recipient who has never held this token.
 */

export const MAX_RECIPIENTS = 1000;

/** Recipients per transaction. Verified empirically against a live validator. */
export const RECIPIENTS_PER_TX_NEW_ATA = 5;
export const RECIPIENTS_PER_TX_EXISTING = 10;

/**
 * Transactions signed per wallet approval.
 *
 * The Wallet Standard `signAllTransactions` takes an array, and wallets show
 * one prompt for it — this is what turns 200 approvals into 20. Kept modest
 * because the whole wave shares one blockhash and must land inside its
 * lifetime, and because how many a given extension will accept in one prompt
 * is the extension's business, not ours.
 */
export const TX_PER_SIGNING_WAVE = 10;

/** Lamports of rent for one SPL token account. */
export const ATA_RENT_LAMPORTS = 2_039_280;
/** Base fee per signature. */
export const SIGNATURE_FEE_LAMPORTS = 5_000;

export interface PlannedRecipient {
  /** 1-based line in the source list, so an error names something findable. */
  line: number;
  address: string;
  /** Raw base units, as a decimal string. */
  raw: string;
  needsAccount: boolean;
}

export interface PlannedBatch {
  index: number;
  recipients: PlannedRecipient[];
  newAccounts: number;
}

export interface DistributionCost {
  recipients: number;
  newAccounts: number;
  batches: number;
  waves: number;
  walletApprovals: number;
  rentLamports: number;
  feeLamports: number;
  totalLamports: number;
}

/**
 * Group recipients into transactions.
 *
 * A batch is flushed when adding the next recipient would exceed the capacity
 * for the batch's *current* composition — and a batch that has any new account
 * in it uses the smaller limit throughout, because mixing is what makes a
 * length calculation wrong in the direction that fails after approval.
 */
export function planBatches(recipients: PlannedRecipient[]): PlannedBatch[] {
  const batches: PlannedBatch[] = [];
  let current: PlannedRecipient[] = [];
  let newAccounts = 0;

  const flush = () => {
    if (!current.length) return;
    batches.push({ index: batches.length, recipients: current, newAccounts });
    current = [];
    newAccounts = 0;
  };

  for (const r of recipients) {
    const wouldHaveNew = newAccounts > 0 || r.needsAccount;
    const capacity = wouldHaveNew ? RECIPIENTS_PER_TX_NEW_ATA : RECIPIENTS_PER_TX_EXISTING;
    if (current.length >= capacity) flush();
    current.push(r);
    if (r.needsAccount) newAccounts++;
  }
  flush();
  return batches;
}

export function estimateCost(batches: PlannedBatch[]): DistributionCost {
  const recipients = batches.reduce((a, b) => a + b.recipients.length, 0);
  const newAccounts = batches.reduce((a, b) => a + b.newAccounts, 0);
  const waves = Math.ceil(batches.length / TX_PER_SIGNING_WAVE);
  const rentLamports = newAccounts * ATA_RENT_LAMPORTS;
  const feeLamports = batches.length * SIGNATURE_FEE_LAMPORTS;
  return {
    recipients,
    newAccounts,
    batches: batches.length,
    waves,
    // One prompt per wave when the wallet supports batch signing.
    walletApprovals: waves,
    rentLamports,
    feeLamports,
    totalLamports: rentLamports + feeLamports,
  };
}

/**
 * Split batches into signing waves. Generic because the console's batches
 * carry built instructions and the CLI's do not — the chunking is the same
 * decision either way, and it should be made in one place.
 */
export function planWaves<T>(batches: T[], perWave = TX_PER_SIGNING_WAVE): T[][] {
  const waves: T[][] = [];
  for (let i = 0; i < batches.length; i += perWave) waves.push(batches.slice(i, i + perWave));
  return waves;
}

// ---------------------------------------------------------------- resume ----

export type LineState = "pending" | "sent" | "failed";

export interface DistributionJournal {
  version: 1;
  mint: string;
  cluster: string;
  startedAt: number;
  /** line -> what happened. Absent means never attempted. */
  lines: Record<number, { state: LineState; sig?: string; error?: string }>;
}

export function emptyJournal(mint: string, cluster: string, now: number): DistributionJournal {
  return { version: 1, mint, cluster, startedAt: now, lines: {} };
}

/**
 * Which recipients still need sending.
 *
 * A distribution of a thousand wallets takes twenty approvals over several
 * minutes. Closing the tab at approval fourteen must not mean guessing which
 * six hundred already went — and must never mean paying six hundred of them
 * twice.
 */
export function remaining(
  recipients: PlannedRecipient[],
  journal: DistributionJournal | null
): PlannedRecipient[] {
  if (!journal) return recipients;
  return recipients.filter((r) => journal.lines[r.line]?.state !== "sent");
}

export function journalSummary(journal: DistributionJournal | null): {
  sent: number;
  failed: number;
} {
  if (!journal) return { sent: 0, failed: 0 };
  const values = Object.values(journal.lines);
  return {
    sent: values.filter((v) => v.state === "sent").length,
    failed: values.filter((v) => v.state === "failed").length,
  };
}

/**
 * A journal only applies to the list it was made for. Resuming a different
 * list against an old journal would skip lines by number rather than by
 * recipient, which is how somebody gets paid twice and somebody else not at
 * all.
 */
export function journalMatches(
  journal: DistributionJournal | null,
  mint: string,
  cluster: string
): boolean {
  return Boolean(journal && journal.mint === mint && journal.cluster === cluster);
}
